import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import API_BASE from "../api";
import {
  getAuthBootstrapTokenCandidates,
  getLegacyAppToken,
} from "../auth/bootstrapTokens";
import "../styles/pages/SupabaseClaimTool.css";

function buildClaimStatus(t, detail, responseBody = null) {
  if (detail === "email_verification_required") {
    return {
      tone: "error",
      title: t("claim_tool_status_verification_title", {
        defaultValue: "Verify your email first",
      }),
      message: t("claim_tool_status_verification_message", {
        defaultValue:
          "The Supabase account must have a verified email before it can be linked to this legacy account.",
      }),
    };
  }

  if (detail === "email_mismatch") {
    return {
      tone: "error",
      title: t("claim_tool_status_email_mismatch_title", {
        defaultValue: "Email mismatch",
      }),
      message: t("claim_tool_status_email_mismatch_message", {
        defaultValue:
          "The verified Supabase email does not match the email on the current legacy account.",
      }),
    };
  }

  if (detail === "supabase_identity_already_claimed") {
    return {
      tone: "error",
      title: t("claim_tool_status_identity_claimed_title", {
        defaultValue: "Supabase identity already claimed",
      }),
      message: t("claim_tool_status_identity_claimed_message", {
        defaultValue:
          "That Supabase account is already linked to a different app user and cannot be claimed here.",
      }),
    };
  }

  if (detail === "account_already_linked") {
    return {
      tone: "error",
      title: t("claim_tool_status_account_linked_title", {
        defaultValue: "Account already linked",
      }),
      message: t("claim_tool_status_account_linked_message", {
        defaultValue:
          "This legacy account is already linked to a different Supabase identity.",
      }),
    };
  }

  if (
    detail === "email_conflict" ||
    detail === "username_conflict" ||
    detail === "profile_email_conflict" ||
    detail === "profile_username_conflict"
  ) {
    return {
      tone: "error",
      title: t("claim_tool_status_conflict_title", {
        defaultValue: "Profile conflict found",
      }),
      message: t("claim_tool_status_conflict_message", {
        defaultValue:
          "A conflicting SQL profile record already exists, so this account needs manual review before linking.",
      }),
    };
  }

  if (detail === "legacy_user_not_found") {
    return {
      tone: "error",
      title: t("claim_tool_status_missing_legacy_title", {
        defaultValue: "Legacy account not found",
      }),
      message: t("claim_tool_status_missing_legacy_message", {
        defaultValue:
          "The current legacy session no longer maps to a legacy account record, so linking could not continue.",
      }),
    };
  }

  if (detail === "Invalid token") {
    return {
      tone: "error",
      title: t("claim_tool_status_invalid_token_title", {
        defaultValue: "Legacy session required",
      }),
      message: t("claim_tool_status_invalid_token_message", {
        defaultValue:
          "Log in with the legacy app session first, then try the claim tool again.",
      }),
    };
  }

  if (detail === "account_link_unavailable") {
    return {
      tone: "error",
      title: t("claim_tool_status_unavailable_title", {
        defaultValue: "Linking unavailable",
      }),
      message: t("claim_tool_status_unavailable_message", {
        defaultValue:
          "The backend could not complete the link right now. Check staging logs and try again.",
      }),
    };
  }

  if (responseBody?.success) {
    return {
      tone: "success",
      title: responseBody.alreadyLinked
        ? t("claim_tool_status_already_linked_title", {
            defaultValue: "Already linked",
          })
        : t("claim_tool_status_success_title", {
            defaultValue: "Link complete",
          }),
      message:
        responseBody.message ||
        t("claim_tool_status_success_message", {
          defaultValue:
            "The Supabase identity is now linked to this existing app account.",
        }),
    };
  }

  return {
    tone: "error",
    title: t("claim_tool_status_unknown_title", {
      defaultValue: "Link failed",
    }),
    message: t("claim_tool_status_unknown_message", {
      defaultValue:
        "The claim request did not complete successfully. Review the response and backend logs for more detail.",
    }),
  };
}

export default function SupabaseClaimTool({ user }) {
  const { t } = useTranslation();

  const legacyToken = useMemo(() => {
    if (typeof window === "undefined") return null;
    return getLegacyAppToken(window);
  }, []);

  const detectedSupabaseToken = useMemo(() => {
    if (typeof window === "undefined") return "";
    return (
      getAuthBootstrapTokenCandidates(window).find(
        (candidate) => candidate.source === "supabase"
      )?.token || ""
    );
  }, []);

  const [supabaseAccessToken, setSupabaseAccessToken] = useState(
    detectedSupabaseToken
  );
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasLegacySession = Boolean(legacyToken);
  const hasDetectedSupabaseToken = Boolean(detectedSupabaseToken);

  const handleLinkSupabase = async (event) => {
    event.preventDefault();

    if (!hasLegacySession) {
      setStatus(
        buildClaimStatus(t, "Invalid token", {
          success: false,
        })
      );
      return;
    }

    const normalizedSupabaseToken = (supabaseAccessToken || "").trim();
    if (!normalizedSupabaseToken) {
      setStatus({
        tone: "error",
        title: t("claim_tool_status_missing_supabase_title", {
          defaultValue: "Supabase token required",
        }),
        message: t("claim_tool_status_missing_supabase_message", {
          defaultValue:
            "Paste a verified Supabase access token or capture one in this browser before trying to link the account.",
        }),
      });
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch(`${API_BASE}/auth/link-supabase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${legacyToken}`,
        },
        body: JSON.stringify({
          supabaseAccessToken: normalizedSupabaseToken,
        }),
      });

      let responseBody = null;
      try {
        responseBody = await response.json();
      } catch {
        responseBody = null;
      }

      if (!response.ok) {
        setStatus(buildClaimStatus(t, responseBody?.detail || "", responseBody));
        return;
      }

      setStatus(buildClaimStatus(t, "", responseBody));
    } catch (error) {
      console.error("Supabase claim tool error:", error);
      setStatus({
        tone: "error",
        title: t("claim_tool_status_network_title", {
          defaultValue: "Request failed",
        }),
        message: t("claim_tool_status_network_message", {
          defaultValue:
            "The request could not reach the backend. Check staging configuration and try again.",
        }),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="claim-tool-page">
      <div className="claim-tool-card">
        <div className="claim-tool-badge">
          {t("claim_tool_badge", {
            defaultValue: "Staging / migration tool",
          })}
        </div>
        <h1 className="claim-tool-title">
          {t("claim_tool_title", {
            defaultValue: "Link existing account to Supabase Auth",
          })}
        </h1>
        <p className="claim-tool-text">
          {t("claim_tool_intro", {
            defaultValue:
              "This tool is intentionally small and manual. It links the currently logged-in legacy account to a verified Supabase identity without changing the app user id.",
          })}
        </p>

        <div className="claim-tool-panel">
          <div className="claim-tool-row">
            <span className="claim-tool-label">
              {t("claim_tool_current_user_label", {
                defaultValue: "Current legacy user",
              })}
            </span>
            <span className="claim-tool-value">
              {user?.username || "-"}
            </span>
          </div>
          <div className="claim-tool-row">
            <span className="claim-tool-label">
              {t("claim_tool_current_email_label", {
                defaultValue: "Legacy account email",
              })}
            </span>
            <span className="claim-tool-value">{user?.email || "-"}</span>
          </div>
          <div className="claim-tool-row">
            <span className="claim-tool-label">
              {t("claim_tool_legacy_session_label", {
                defaultValue: "Legacy session",
              })}
            </span>
            <span
              className={`claim-tool-pill ${
                hasLegacySession
                  ? "claim-tool-pill--success"
                  : "claim-tool-pill--error"
              }`}
            >
              {hasLegacySession
                ? t("claim_tool_legacy_session_ready", {
                    defaultValue: "Ready",
                  })
                : t("claim_tool_legacy_session_missing", {
                    defaultValue: "Missing",
                  })}
            </span>
          </div>
          <div className="claim-tool-row">
            <span className="claim-tool-label">
              {t("claim_tool_detected_supabase_label", {
                defaultValue: "Detected Supabase token",
              })}
            </span>
            <span
              className={`claim-tool-pill ${
                hasDetectedSupabaseToken
                  ? "claim-tool-pill--success"
                  : "claim-tool-pill--neutral"
              }`}
            >
              {hasDetectedSupabaseToken
                ? t("claim_tool_detected_supabase_found", {
                    defaultValue: "Detected in this browser",
                  })
                : t("claim_tool_detected_supabase_missing", {
                    defaultValue: "Not detected",
                  })}
            </span>
          </div>
        </div>

        <form className="claim-tool-form" onSubmit={handleLinkSupabase}>
          <label className="claim-tool-field">
            <span className="claim-tool-field-label">
              {t("claim_tool_access_token_label", {
                defaultValue: "Supabase access token",
              })}
            </span>
            <textarea
              value={supabaseAccessToken}
              onChange={(event) => setSupabaseAccessToken(event.target.value)}
              className="claim-tool-textarea"
              rows={7}
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              placeholder={t("claim_tool_access_token_placeholder", {
                defaultValue:
                  "Paste a verified Supabase access token here, or rely on the detected bridge token if one is already present.",
              })}
            />
          </label>

          {hasDetectedSupabaseToken && detectedSupabaseToken !== supabaseAccessToken ? (
            <button
              type="button"
              className="claim-tool-secondary-button"
              onClick={() => setSupabaseAccessToken(detectedSupabaseToken)}
            >
              {t("claim_tool_use_detected_token", {
                defaultValue: "Use detected Supabase token",
              })}
            </button>
          ) : null}

          <button
            type="submit"
            className="claim-tool-button"
            disabled={isSubmitting || !hasLegacySession}
          >
            {isSubmitting
              ? t("claim_tool_button_submitting", {
                  defaultValue: "Linking...",
                })
              : t("claim_tool_button_default", {
                  defaultValue: "Link Supabase account",
                })}
          </button>
        </form>

        {status ? (
          <div
            className={`claim-tool-status claim-tool-status--${status.tone}`}
            role="status"
            aria-live="polite"
          >
            <div className="claim-tool-status-title">{status.title}</div>
            <p className="claim-tool-status-text">{status.message}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
